import { ICommandBarItemProps, Separator } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPCommandBar,
  IPDefaultButton,
  IPLabel,
  IPPrimaryButton,
  IPSeparator,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import HrefLink from 'components/Links/HrefLink';
import OverlayLoader from 'components/Loading/OverlayLoader';
import InfoMessage from 'components/Notification/InfoMessage';
import { globalAppName } from 'globalConstants';
import { onRenderLabelWithInfo } from 'globalFunctions';
import { globalStackTokensGapSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import ReportRequestDTO, { ReportOutputFormat, ReportStorageBehavior } from 'models/dto/Reports/ReportRequestDTO';
import { ReportType } from 'models/reports/ReportDefinition';
import { DocumentProviderType } from 'models/documentProvider';
import { DocumentOutputPurpose } from 'models/documentOutputLocation';
import ResourceList from 'models/resourceList';
import { ReportAllowUserSettingOverride, SharepointReportsAndPublishing } from 'models/setting';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiCheckSharePointReportWriteAccess, apiGetReport } from 'services/Api/reportService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { toLocaleDateNumeric } from 'utils/datetime';
import { downloadBlob } from 'utils/download';
import ReportOptions15 from './Options/ReportOptions15';
import ReportOptions2 from './Options/ReportOptions2';
import ReportOptions6 from './Options/ReportOptions6';
import { getCommandBarItemsReportOutputFormat, getCommandBarItemsReportStorageBehavior } from './ReportContextMenu';

export interface IReportRequestOptions {
  reportRequest?: ReportRequestDTO;
  onUpdate: (reportRequest: ReportRequestDTO, isValid: boolean) => void;
}

interface IReportRequestProps {
  isOpen: boolean;
  isLoading?: boolean;
  reportRequest?: ReportRequestDTO;
  onClose: () => void;
  onGoToSettings?: () => void;
}

const ReportRequest = (props: IReportRequestProps) => {
  const { t } = useTranslation(['translation', 'adminReports']);
  const appContext = useContext(AppContext);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOptionsValid, setIsOptionsValid] = useState<boolean>(true);
  const [hasWriteAccess, setHasWriteAccess] = useState<boolean>(false);
  const [spReportsEnabled, setSpReportsEnabled] = useState<boolean>(false);
  const [currentReportRequest, setCurrentReportRequest] = useState<ReportRequestDTO | undefined>(props.reportRequest);
  const [reportList, setReportList] = useState<ResourceList | undefined>(undefined);
  const [reportUrl, setReportUrl] = useState<string | undefined>(undefined);
  const history = useHistory();

  useEffect(() => {
    if (props.isOpen && props.reportRequest) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.reportRequest]);

  const isOutputFormatAllowed = (
    reportDefinition: ReportRequestDTO['reportDefinition'],
    outputFormat: ReportOutputFormat,
    sharePointEnabled: boolean,
  ): boolean => {
    switch (outputFormat) {
      case ReportOutputFormat.Word:
        return reportDefinition?.canExportWord() ?? true;
      case ReportOutputFormat.Excel:
        return reportDefinition?.canExportExcel() ?? true;
      case ReportOutputFormat.PDF:
        return reportDefinition?.canExportPdf(sharePointEnabled) ?? sharePointEnabled;
    }
  };

  //output to the location makes its PDF there, so the provider of the category must convert to PDF; output
  //Download makes its PDF in the user's own OneDrive
  const isPdfPossible = (
    sharePointAvailable: boolean,
    storageBehavior: ReportStorageBehavior,
    list: ResourceList | undefined,
  ): boolean => {
    if (!sharePointAvailable) return false;
    if (storageBehavior !== ReportStorageBehavior.SharePoint || !list) return true;

    return appContext.globalDataCache.documentProviders.getCapabilities(
      list.providerType ?? DocumentProviderType.SharePoint,
    ).pdfConversion;
  };

  const getFallbackOutputFormat = (
    reportDefinition: ReportRequestDTO['reportDefinition'],
    sharePointEnabled: boolean,
  ): ReportOutputFormat => {
    const preferredFormats = [ReportOutputFormat.Word, ReportOutputFormat.Excel, ReportOutputFormat.PDF];

    for (const outputFormat of preferredFormats) {
      if (isOutputFormatAllowed(reportDefinition, outputFormat, sharePointEnabled)) {
        return outputFormat;
      }
    }

    return ReportOutputFormat.Word;
  };

  const loadData = async () => {
    try {
      setReportUrl(undefined);
      setIsLoading(true);

      //get the setting if SharePoint integration is enabled
      const spReportsEnabled = appContext.globalDataCache.settings.get(SharepointReportsAndPublishing) as boolean;
      setSpReportsEnabled(spReportsEnabled);

      //the category of the general reports location
      await appContext.globalDataCache.outputLocations.getItems();
      const listId = appContext.globalDataCache.outputLocations.get(DocumentOutputPurpose.GeneralReports)?.listId;
      let writeException: string | undefined = undefined;
      let writeAccess: boolean = false;
      let validList: boolean = false;
      let boundList: ResourceList | undefined = undefined;

      //When SharePoint is enabled but no list is set, show no warning
      if (spReportsEnabled && listId) {
        //check list
        await appContext.globalDataCache.lists.getItems();
        const list = appContext.globalDataCache.lists.get(listId);
        if (list && list.listId) {
          setReportList(list);
          boundList = list;
          validList = true;

          //check whether the user is allowed to write to the SharePoint location. If not give warning and set default output to Word/Download
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          writeException = await apiCheckSharePointReportWriteAccess(accessToken);
          if (!writeException) writeAccess = true;
          setHasWriteAccess(writeAccess);
        }

        //Log results
        if (!validList || !writeAccess) {
          logger.debug('User has no write access or configuration is invalid.', writeException);
          if (!validList) logger.debug(`Report list is not valid: ${listId}`);
        }
      }

      if (props.reportRequest) {
        const newReportRequest = props.reportRequest.clone();
        const sharePointAvailable = spReportsEnabled && writeAccess;

        //when the user has no write access, set the report settings to download and normalize the format
        if (!sharePointAvailable) {
          newReportRequest.storageBehavior = ReportStorageBehavior.Download;
        }

        const pdfPossible = isPdfPossible(sharePointAvailable, newReportRequest.storageBehavior, boundList);
        if (!isOutputFormatAllowed(newReportRequest.reportDefinition, newReportRequest.outputFormat, pdfPossible)) {
          newReportRequest.outputFormat = getFallbackOutputFormat(newReportRequest.reportDefinition, pdfPossible);
        }

        setCurrentReportRequest(newReportRequest);
      } else {
        setCurrentReportRequest(props.reportRequest);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const startReport = async () => {
    try {
      if (!currentReportRequest) return;
      setIsReportLoading(true);
      currentReportRequest.fileName = createFileName(currentReportRequest);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const report = await apiGetReport(currentReportRequest, accessToken);
      showReport(currentReportRequest, report);
    } catch (err) {
      const reportErr = err as AppError;
      const msg = getReportErrorMsg(reportErr);
      if (msg) {
        appContext.showNotification(msg, true);
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsReportLoading(false);
    }
  };

  const getReportErrorMsg = (err: AppError): string | undefined => {
    if (!err || !err.debug) return undefined;

    if (err.debug.indexOf('accessDenied') >= 0) {
      return t('translation:Errors.AccessDenied.Message');
    } else if (
      err.debug.indexOf('itemNotFound') >= 0 ||
      err.debug.indexOf('templateLinkNotFound') >= 0 ||
      err.debug.indexOf('templateListNotFound') >= 0
    ) {
      return t('adminReports:Errors.TemplateNotFound');
    }

    return undefined;
  };

  const showReport = (reportDto: ReportRequestDTO, report: Blob | string | undefined) => {
    if (typeof report === 'string') {
      if (!report) {
        props.onClose();
        appContext.showNotification(t('translation:Errors.EmptyReport.Message'), true);
      } else {
        setReportUrl(report);
      }
    } else {
      if (!report || report.size === 0) {
        appContext.showNotification(t('translation:Errors.EmptyReport.Message'), true);
      } else {
        downloadBlob(report, reportDto.fileName ?? createFileName(reportDto));
      }
      props.onClose();
    }
  };

  const createFileName = (reportDto: ReportRequestDTO): string => {
    let ext: string;
    switch (reportDto.outputFormat) {
      case ReportOutputFormat.Word:
        ext = '.docx';
        break;
      case ReportOutputFormat.PDF:
        ext = '.pdf';
        break;
      case ReportOutputFormat.Excel:
        ext = '.xlsx';
        break;
    }

    let baseName = t('adminReports:BaseFileName', { appName: globalAppName });
    baseName += reportDto.reportDefinition?.name + '-';
    baseName += toLocaleDateNumeric(new Date());

    return baseName + ext;
  };

  const getCommandBarFormatItems = (): ICommandBarItemProps[] => {
    if (!currentReportRequest) return [];
    const items: ICommandBarItemProps[] = [];
    const sharePointEnabled = getSharePointEnabled();

    items.push(
      getCommandBarItemsReportOutputFormat(
        isPdfPossible(sharePointEnabled, currentReportRequest.storageBehavior, reportList),
        currentReportRequest.reportDefinition,
        currentReportRequest.outputFormat,
        {
          disabled: !canChangeSettings(),
          onSelect: (outputFormat) => {
            const newReportRequest = currentReportRequest.clone();
            newReportRequest.outputFormat = outputFormat;
            setCurrentReportRequest(newReportRequest);
            setReportUrl(undefined);
          },
        },
        t as TFunction<string[]>,
      ),
    );

    if (sharePointEnabled) {
      items.push(
        getCommandBarItemsReportStorageBehavior(
          currentReportRequest.storageBehavior,
          {
            disabled: !canChangeSettings(),
            onSelect: (storageBehavior) => {
              const newReportRequest = currentReportRequest.clone();
              newReportRequest.storageBehavior = storageBehavior;
              const pdfPossible = isPdfPossible(sharePointEnabled, storageBehavior, reportList);
              if (
                !isOutputFormatAllowed(newReportRequest.reportDefinition, newReportRequest.outputFormat, pdfPossible)
              ) {
                newReportRequest.outputFormat = getFallbackOutputFormat(newReportRequest.reportDefinition, pdfPossible);
              }
              setCurrentReportRequest(newReportRequest);
              setReportUrl(undefined);
            },
          },
          t as TFunction<string[]>,
        ),
      );
    }

    return items;
  };

  const getSharePointEnabled = (): boolean => {
    return spReportsEnabled && reportList !== undefined && hasWriteAccess;
  };

  const canChangeSettings = (): boolean => {
    return appContext.globalDataCache.settings.get(ReportAllowUserSettingOverride) as boolean;
  };

  const openReportSettings = () => {
    if (props.onGoToSettings) {
      props.onGoToSettings();
    } else {
      history.push('/admin/settings/reports');
    }
  };

  const getFormatSettings = (): JSX.Element | null => {
    if (isLoading) return null;

    return (
      <IPStack>
        <IPLabel>{t('adminReports:Dialogs.Request.Settings')}</IPLabel>
        <IPCommandBar styles={{ root: { padding: 0 } }} items={getCommandBarFormatItems()}></IPCommandBar>
      </IPStack>
    );
  };

  const getLocationInfo = () => {
    if (
      isLoading ||
      currentReportRequest?.storageBehavior !== ReportStorageBehavior.SharePoint ||
      !getSharePointEnabled()
    ) {
      return null;
    }

    return (
      <IPStack>
        {onRenderLabelWithInfo(
          t('adminReports:Dialogs.Request.Location.Label'),
          t('adminReports:Dialogs.Request.Location.Info'),
        )}
        <IPStack>
          {reportList && (
            <HrefLink href={reportList.webURL} external>
              {reportList?.name}
            </HrefLink>
          )}
          {!reportList && hasUserFeatureGenericManager(appContext) && (
            <HrefLink href={'/admin/settings/reports'} onClick={openReportSettings}>
              {t('adminReports:Dialogs.Request.Location.NotSetRole')}
            </HrefLink>
          )}
          {!reportList && !hasUserFeatureGenericManager(appContext) && (
            <IPText>{t('adminReports:Dialogs.Request.Location.NotSet')}</IPText>
          )}
        </IPStack>
      </IPStack>
    );
  };

  const getWriteAccessWarning = (): JSX.Element | null => {
    if (isLoading) return null;
    if (!spReportsEnabled || !reportList) return null;
    if (reportList && hasWriteAccess) return null;

    return (
      <InfoMessage message={t('adminReports:Dialogs.Request.NoWriteAccess')}>
        {reportList && (
          <IPStack styles={{ root: { paddingTop: 10 } }}>
            <HrefLink href={reportList.webURL} external>
              {reportList.name}
            </HrefLink>
          </IPStack>
        )}
      </InfoMessage>
    );
  };

  const onUpdateOptions = (reportRequest: ReportRequestDTO, isValid: boolean) => {
    setCurrentReportRequest(reportRequest);
    setIsOptionsValid(isValid);
  };

  const getReportSpecificOptionsWrapper = (options: JSX.Element | null): JSX.Element | null => {
    return (
      <Fragment>
        {options}
        <Separator />
      </Fragment>
    );
  };

  const getReportSpecificOptions = (): JSX.Element | null => {
    if (isLoading || props.isLoading) return null;
    let options: JSX.Element | null = null;

    switch (currentReportRequest?.reportDefinition?.id) {
      case ReportType.SOA_Controls:
        options = <ReportOptions6 reportRequest={currentReportRequest} onUpdate={onUpdateOptions} />;
        break;
      case ReportType.LibraryPublicationHistory:
        options = <ReportOptions15 reportRequest={currentReportRequest} onUpdate={onUpdateOptions} />;
        break;
      case ReportType.RiskTreatmentPlan:
        options = <ReportOptions2 reportRequest={currentReportRequest} onUpdate={onUpdateOptions} />;
        break;
      default:
        return null;
    }

    return getReportSpecificOptionsWrapper(options);
  };

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      title={props.reportRequest?.reportDefinition?.name ?? ''}
      onClose={props.onClose}
      verticalGap={globalStackTokensGapSmall}
      width={'50vw'}
      minHeight={460}
      height={(props.reportRequest?.reportDefinition?.optionsHeight ?? 0) + 460}
      maxHeight={'90vh'}
      maxWidth={500}
      hideFooter={true}
    >
      {isReportLoading && <OverlayLoader text={t('adminReports:Dialogs.Request.ReportLoading')} />}
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText variant="small">{props.reportRequest?.reportDefinition?.description}</IPText>
        </IPStack.Item>
        <IPSeparator />
        <IPStack.Item grow>
          <IPStack tokens={globalStackTokensGapSmall}>
            {isLoading && <IPSpinner size={IPSpinnerSize.large} />}
            {getReportSpecificOptions()}
            {getFormatSettings()}
            {getLocationInfo()}
            {getWriteAccessWarning()}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPSeparator />
          <IPStack
            horizontal
            horizontalAlign="end"
            tokens={globalStackTokensGapSmall}
            styles={{ root: { paddingBottom: 10 } }}
          >
            {!reportUrl && (
              <IPPrimaryButton
                disabled={isReportLoading || isLoading || props.isLoading || !isOptionsValid}
                onClick={async () => {
                  await startReport();
                }}
                text={t('translation:General.Button.Create')}
                onRenderIcon={
                  !props.isLoading
                    ? undefined
                    : (props, defaultRender) => {
                        return <IPSpinner />;
                      }
                }
              />
            )}
            {reportUrl && !isReportLoading && !isLoading && (
              <HrefLink href={reportUrl} underline external>
                {t('adminReports:Dialogs.Request.OpenReportUrl')}
              </HrefLink>
            )}
            <IPDefaultButton
              disabled={isReportLoading || isLoading}
              onClick={() => {
                props.onClose();
              }}
              text={t('translation:General.Button.Cancel')}
            ></IPDefaultButton>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};

export default ReportRequest;
